/**
 * E타입 - 수식 입력 컴포넌트
 * 손글씨 캔버스 + Mathpix OCR + 학생 확인
 */

import React, { useState, useRef, useEffect } from 'react';
import { PixelButton } from '../common/PixelButton';
import { PixelCanvas, canvasToBlob, clearCanvas } from '../canvas/PixelCanvas';
import { DrawingTools } from '../canvas/DrawingTools';
import axiosInstance from '../../config/axios';
// @ts-ignore
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * EquationInput Props
 */
interface EquationInputProps {
  /** 답안 제출 콜백 */
  onSubmit: (answer: string) => void;
  /** OCR 결과 확인 콜백 */
  onOcrResult?: (latex: string) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * E타입 수식 입력 컴포넌트
 *
 * 플로우:
 * 1. 손글씨로 수식 그리기
 * 2. OCR 인식 → LaTeX 결과 표시
 * 3. 학생 확인 → 맞으면 제출, 아니면 다시 그리기
 */
export const EquationInput: React.FC<EquationInputProps> = ({
  onSubmit,
  onOcrResult,
  disabled = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mathRef = useRef<HTMLDivElement>(null);

  const [isEraser, setIsEraser] = useState(false);
  const [penWidth, setPenWidth] = useState(3);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState<string>('');
  const [isConfirmMode, setIsConfirmMode] = useState(false);

  /**
   * LaTeX 정리 (KaTeX 렌더링용)
   */
  const cleanLatex = (latex: string): string => {
    return latex.replace(/\\\(|\\\)/g, '').trim();
  };

  /**
   * KaTeX로 수식 렌더링
   */
  useEffect(() => {
    if (ocrResult && isConfirmMode && mathRef.current) {
      console.log('🎨 KaTeX 렌더링 시도:', ocrResult);
      console.log('🎨 정리된 LaTeX:', cleanLatex(ocrResult));

      try {
        const cleaned = cleanLatex(ocrResult);
        mathRef.current.innerHTML = ''; // 초기화

        katex.render(cleaned, mathRef.current, {
          throwOnError: false,
          displayMode: true,
        });

        console.log('✅ KaTeX 렌더링 성공');
      } catch (error) {
        console.error('❌ KaTeX 렌더링 실패:', error);
        // 실패 시 원본 텍스트 표시
        mathRef.current.textContent = ocrResult;
      }
    }
  }, [ocrResult, isConfirmMode]);

  /**
   * 전체 지우기
   */
  const handleClear = () => {
    if (canvasRef.current) {
      clearCanvas(canvasRef.current);
      setOcrResult('');
      setIsConfirmMode(false);
    }
  };

  /**
   * OCR 처리 (1단계: 인식)
   */
  const handleRecognize = async () => {
    if (!canvasRef.current || disabled) return;

    try {
      setIsProcessing(true);

      const blob = await canvasToBlob(canvasRef.current);

      const formData = new FormData();
      formData.append('file', blob, 'equation.png');
      formData.append('answer_format', 'E');

      const response = await axiosInstance.post('/ocr/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const result = response.data;
      const recognizedText = result.text || result.latex || '';

      if (!recognizedText) {
        alert('수식을 인식하지 못했어요 😢 다시 그려주세요!');
        return;
      }

      setOcrResult(recognizedText);
      setIsConfirmMode(true);

      // OCR 결과를 부모에게 전달 (채팅봇 메시지용)
      if (onOcrResult) {
        onOcrResult(recognizedText);
      }
    } catch (error: any) {
      console.error('OCR 처리 실패:', error);
      alert('수식 인식에 실패했어요 😢 다시 그려주세요!');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 수식 확인 후 제출 (2단계: 채점)
   */
  const handleConfirmAndSubmit = () => {
    if (!ocrResult) return;
    onSubmit(ocrResult);
    setOcrResult('');
    setIsConfirmMode(false);
  };

  /**
   * 다시 그리기
   */
  const handleRetry = () => {
    handleClear();
  };

  return (
    <div
      className="space-y-2 select-none"
      onDragStart={(e) => e.preventDefault()}
    >
      {/* 드로잉 도구 */}
      <DrawingTools
        isEraser={isEraser}
        onToggleEraser={() => setIsEraser(!isEraser)}
        penWidth={penWidth}
        onPenWidthChange={setPenWidth}
        onClear={handleClear}
      />

      {/* 캔버스 */}
      <div
        className="flex justify-center"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        style={{ touchAction: 'none' }}
      >
        <PixelCanvas
          ref={canvasRef}
          responsive={true}
          smoothing={true}
          penColor="#000000"
          penWidth={penWidth}
          isEraser={isEraser}
        />
      </div>

      {/* 버튼 (모드별) */}
      {!isConfirmMode ? (
        <PixelButton
          variant="primary"
          size="small"
          onClick={handleRecognize}
          disabled={disabled || isProcessing}
          className="w-full"
        >
          {isProcessing ? 'OCR 인식 중...' : 'OCR 인식'}
        </PixelButton>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <PixelButton
            variant="secondary"
            size="small"
            onClick={handleRetry}
            disabled={disabled}
          >
            다시 그리기
          </PixelButton>
          <PixelButton
            variant="primary"
            size="small"
            onClick={handleConfirmAndSubmit}
            disabled={disabled}
          >
            맞아요! 제출
          </PixelButton>
        </div>
      )}
    </div>
  );
};
